import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Search as SearchIcon, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import PhoneFrame from '../components/PhoneFrame';
import StarField from '../components/StarField';
import { GalaxyMark } from '../components/OrbitLogo';

export default function ExploreGalaxies() {
  const nav = useNavigate();
  const {
    publicGalaxies,
    publicGalaxiesLoading,
    loadPublicGalaxies,
    joinPublicGalaxy,
  } = useApp();
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState({});

  useEffect(() => {
    loadPublicGalaxies({ excludeMine: false }).catch((err) => toast(err.message || 'Could not load Galaxies'));
  }, [loadPublicGalaxies]);

  const join = async (event, galaxy) => {
    event.stopPropagation();
    if (galaxy.joined || pending[galaxy.id]) return;
    setPending((value) => ({ ...value, [galaxy.id]: true }));
    try {
      await joinPublicGalaxy(galaxy.id);
      toast(`Joined ${galaxy.name}`);
    } catch (err) {
      toast(err.message || 'Could not join this Galaxy');
    } finally {
      setPending((value) => ({ ...value, [galaxy.id]: false }));
    }
  };

  const filtered = publicGalaxies.filter((galaxy) => galaxy.name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <PhoneFrame>
      <div className="relative min-h-full">
        <StarField />
        <div className="relative z-10">
          <div className="orbit-page-header">
            <button className="press orbit-icon-button" onClick={() => nav(-1)} aria-label="Go back">
              <ArrowLeft />
            </button>
            <h1 className="font-display orbit-page-title flex-1 text-center">Explore Galaxies</h1>
            <div className="w-10" aria-hidden="true" />
          </div>
          <div className="px-4 py-4">
            <div className="orbit-field flex items-center gap-2" style={{ minHeight: 40, padding: '8px 16px', background: 'rgba(255,255,255,.06)' }}>
              <SearchIcon size={18} color="#9d92c4" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter Galaxies…"
                className="flex-1 bg-transparent outline-none placeholder:text-[#7d769f]"
                style={{ color: '#efeaff' }}
              />
            </div>
          </div>
          <div className="h-px w-full" style={{ background: 'linear-gradient(90deg,transparent,rgba(201,162,75,.5),transparent)' }} />

          <div className="p-4">
            {publicGalaxiesLoading && <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>Loading…</p>}
            {!publicGalaxiesLoading && filtered.length === 0 && (
              <div className="flex flex-col items-center py-10">
                <div className="floaty mb-3"><GalaxyMark size={44} /></div>
                <p className="text-center italic" style={{ color: '#6f6795' }}>
                  {query.trim() ? 'No Galaxies match that signal.' : 'No public Galaxies are broadcasting yet.'}
                </p>
              </div>
            )}
            {filtered.map((galaxy) => (
              <div
                key={galaxy.id}
                onClick={() => nav(`/galaxies/${galaxy.id}`)}
                className="press orbit-surface-card flex items-center gap-3 mb-3 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-full shrink-0" style={{ background: 'radial-gradient(circle at 40% 35%, #8b7ce8, transparent 75%)', boxShadow: '0 0 16px #8b7ce8' }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold capitalize truncate" style={{ color: '#f1ecff' }}>{galaxy.name}</div>
                  <div className="text-sm" style={{ color: '#8079a6' }}>{galaxy.memberCount} {galaxy.memberCount === 1 ? 'member' : 'members'}</div>
                </div>
                <button
                  onClick={(event) => join(event, galaxy)}
                  disabled={galaxy.joined || pending[galaxy.id]}
                  className={`press !px-4 ${galaxy.joined ? 'orbit-secondary-button' : 'orbit-primary-button'}`}
                  style={{ opacity: pending[galaxy.id] ? 0.6 : 1 }}
                >
                  {galaxy.joined ? <><Check /> Joined</> : <><UserPlus /> Join</>}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
